import { supabase } from './supabaseService';
import { Session } from '../types/multiTenant';

/**
 * Session Service
 * Handles all session-related operations with Supabase
 * Sessions are automatically filtered by organization_id via RLS
 */
class SessionService {
  /**
   * List all sessions for an organization
   */
  async listSessions(
    organizationId: string,
    filters?: {
      clientId?: string;
      conductedBy?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<Session[]> {
    try {
      let query = supabase
        .from('sessions')
        .select(`
          *,
          client:clients(id, full_name),
          conductor:users!sessions_conducted_by_fkey(id, full_name),
          appointment:appointments(id, title)
        `)
        .eq('organization_id', organizationId)
        .eq('deleted', false);

      if (filters?.clientId) {
        query = query.eq('client_id', filters.clientId);
      }

      if (filters?.conductedBy) {
        query = query.eq('conducted_by', filters.conductedBy);
      }

      if (filters?.startDate) {
        query = query.gte('session_date', filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte('session_date', filters.endDate);
      }

      query = query.order('session_date', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Error listing sessions:', error);
        return [];
      }

      return (data || []).map(this.transformSession);
    } catch (error) {
      console.error('Error in listSessions:', error);
      return [];
    }
  }

  /**
   * Get session by ID
   */
  async getSessionById(sessionId: string): Promise<Session | null> {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          client:clients(id, full_name),
          conductor:users!sessions_conducted_by_fkey(id, full_name),
          appointment:appointments(id, title)
        `)
        .eq('id', sessionId)
        .eq('deleted', false)
        .single();

      if (error) {
        console.error('Error fetching session:', error);
        return null;
      }

      return this.transformSession(data);
    } catch (error) {
      console.error('Error in getSessionById:', error);
      return null;
    }
  }

  /**
   * Get sessions by client
   */
  async getSessionsByClient(clientId: string): Promise<Session[]> {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          client:clients(id, full_name),
          conductor:users!sessions_conducted_by_fkey(id, full_name),
          appointment:appointments(id, title)
        `)
        .eq('client_id', clientId)
        .eq('deleted', false)
        .order('session_date', { ascending: false });

      if (error) {
        console.error('Error fetching sessions by client:', error);
        return [];
      }

      return (data || []).map(this.transformSession);
    } catch (error) {
      console.error('Error in getSessionsByClient:', error);
      return [];
    }
  }

  /**
   * Create new session
   */
  async createSession(data: {
    organizationId: string;
    clientId: string;
    appointmentId?: string;
    conductedBy: string;
    sessionDate: string;
    durationMinutes?: number;
    title?: string;
    content: string;
    attachments?: any[];
    aiSummary?: string;
    aiInsights?: any;
    customData?: Record<string, any>;
    tags?: string[];
    createdBy: string;
  }): Promise<Session | null> {
    try {
      const { data: sessionData, error } = await supabase
        .from('sessions')
        .insert({
          organization_id: data.organizationId,
          client_id: data.clientId,
          appointment_id: data.appointmentId || null,
          conducted_by: data.conductedBy || data.createdBy || null,
          session_date: data.sessionDate,
          duration_minutes: data.durationMinutes || 60,
          title: data.title,
          content: data.content,
          attachments: data.attachments || [],
          ai_summary: data.aiSummary,
          ai_insights: data.aiInsights,
          custom_data: data.customData || {},
          tags: data.tags || [],
          created_by: data.createdBy,
        })
        .select(`
          *,
          client:clients(id, full_name),
          conductor:users!sessions_conducted_by_fkey(id, full_name),
          appointment:appointments(id, title)
        `)
        .single();

      if (error) {
        console.error('Error creating session:', error);
        return null;
      }

      return this.transformSession(sessionData);
    } catch (error) {
      console.error('Error in createSession:', error);
      return null;
    }
  }

  /**
   * Update session
   */
  async updateSession(
    sessionId: string,
    updates: Partial<Session>
  ): Promise<Session | null> {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .update({
          appointment_id: updates.appointmentId === '' ? null : updates.appointmentId,
          session_date: updates.sessionDate,
          duration_minutes: updates.durationMinutes,
          title: updates.title,
          content: updates.content,
          attachments: updates.attachments,
          ai_summary: updates.aiSummary,
          ai_insights: updates.aiInsights,
          custom_data: updates.customData,
          tags: updates.tags,
        })
        .eq('id', sessionId)
        .select(`
          *,
          client:clients(id, full_name),
          conductor:users!sessions_conducted_by_fkey(id, full_name),
          appointment:appointments(id, title)
        `)
        .single();

      if (error) {
        console.error('Error updating session:', error);
        return null;
      }

      return this.transformSession(data);
    } catch (error) {
      console.error('Error in updateSession:', error);
      return null;
    }
  }

  /**
   * Delete session (soft delete)
   */
  async deleteSession(sessionId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .update({
          deleted: true,
        })
        .eq('id', sessionId)
        .select();

      if (error) {
        console.error('Error deleting session:', error);
        return false;
      }

      // Return true only if at least one row was affected
      return data && data.length > 0;
    } catch (error) {
      console.error('Error in deleteSession:', error);
      return false;
    }
  }

  /**
   * Search sessions with full-text search
   */
  async searchSessions(
    organizationId: string,
    query: string
  ): Promise<Session[]> {
    try {
      // Use PostgreSQL full-text search
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          client:clients(id, full_name),
          conductor:users!sessions_conducted_by_fkey(id, full_name),
          appointment:appointments(id, title)
        `)
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .textSearch('fts', query, {
          type: 'websearch',
          config: 'portuguese',
        })
        .order('session_date', { ascending: false });

      if (error) {
        // Fallback to ILIKE search if full-text search fails
        return this.searchSessionsILike(organizationId, query);
      }

      return (data || []).map(this.transformSession);
    } catch (error) {
      console.error('Error in searchSessions:', error);
      return this.searchSessionsILike(organizationId, query);
    }
  }

  /**
   * Fallback search using ILIKE
   */
  private async searchSessionsILike(
    organizationId: string,
    query: string
  ): Promise<Session[]> {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          client:clients(id, full_name),
          conductor:users!sessions_conducted_by_fkey(id, full_name),
          appointment:appointments(id, title)
        `)
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .or(`title.ilike.%${query}%,content.ilike.%${query}%`)
        .order('session_date', { ascending: false });

      if (error) {
        console.error('Error in ILIKE search:', error);
        return [];
      }

      return (data || []).map(this.transformSession);
    } catch (error) {
      console.error('Error in searchSessionsILike:', error);
      return [];
    }
  }

  /**
   * Get sessions by tag
   */
  async getSessionsByTag(
    organizationId: string,
    tag: string
  ): Promise<Session[]> {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          client:clients(id, full_name),
          conductor:users!sessions_conducted_by_fkey(id, full_name),
          appointment:appointments(id, title)
        `)
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .contains('tags', [tag])
        .order('session_date', { ascending: false });

      if (error) {
        console.error('Error fetching sessions by tag:', error);
        return [];
      }

      return (data || []).map(this.transformSession);
    } catch (error) {
      console.error('Error in getSessionsByTag:', error);
      return [];
    }
  }

  /**
   * Get session statistics
   */
  async getSessionStats(
    organizationId: string,
    startDate?: string,
    endDate?: string
  ): Promise<{
    total: number;
    byClient: Record<string, number>;
    byTag: Record<string, number>;
    withAiInsights: number;
  }> {
    try {
      const sessions = await this.listSessions(organizationId, {
        startDate,
        endDate,
      });

      const stats = {
        total: sessions.length,
        byClient: {} as Record<string, number>,
        byTag: {} as Record<string, number>,
        withAiInsights: 0,
      };

      sessions.forEach((session) => {
        // Count by client
        if (session.clientId) {
          stats.byClient[session.clientId] =
            (stats.byClient[session.clientId] || 0) + 1;
        }

        // Count by tags
        if (session.tags) {
          session.tags.forEach((tag) => {
            stats.byTag[tag] = (stats.byTag[tag] || 0) + 1;
          });
        }

        // Count with AI insights
        if (session.aiInsights) {
          stats.withAiInsights++;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error in getSessionStats:', error);
      return {
        total: 0,
        byClient: {},
        byTag: {},
        withAiInsights: 0,
      };
    }
  }

  /**
   * Transform database session to app format
   */
  private transformSession(data: any): Session {
    return {
      id: data.id,
      organizationId: data.organization_id,
      clientId: data.client_id,
      appointmentId: data.appointment_id,
      conductedBy: data.conducted_by,
      sessionDate: data.session_date,
      durationMinutes: data.duration_minutes,
      title: data.title,
      content: data.content,
      attachments: data.attachments || [],
      aiSummary: data.ai_summary,
      aiInsights: data.ai_insights,
      customData: data.custom_data || {},
      tags: data.tags || [],
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      deleted: data.deleted || false,
    };
  }
}

export const sessionService = new SessionService();
export default sessionService;
